/* UnifiedRevenueWidget — single trend chart joining Academy + Systems +
 * Services revenue. Mounted at the top of the Revenue tab.
 *
 * Reads /api/admin/revenue/unified which aggregates `payment_transactions`
 * (kind=academy|systems) AND `services_transactions` into one day/week/hour
 * series with division splits.
 *
 * Pure SVG chart — no Recharts dependency. Stacked-area visualisation with
 * hover tooltip + division filter pills.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TrendingUp, TrendingDown, RefreshCw, GraduationCap, Wrench, ShoppingBag,
  DollarSign, Activity,
} from 'lucide-react';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

const DIVISIONS = [
  { id: 'academy',  label: 'Academy',  color: '#9333ea', Icon: GraduationCap },
  { id: 'systems',  label: 'Systems',  color: '#f5a623', Icon: Wrench },
  { id: 'services', label: 'Services', color: '#10b981', Icon: ShoppingBag },
];

const RANGES = [
  { id: 7,   label: '7d' },
  { id: 30,  label: '30d' },
  { id: 90,  label: '90d' },
  { id: 365, label: '1y' },
];

const fmtUSD = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

function formatBucket(b, granularity) {
  if (!b) return '';
  if (granularity === 'hour') return b.slice(5, 16).replace('T', ' ');
  return b.slice(5);  // MM-DD
}

export default function UnifiedRevenueWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [hideDivs, setHideDivs] = useState(new Set());
  const [hoverIdx, setHoverIdx] = useState(null);

  const granularity = days <= 7 ? 'day' : days <= 90 ? 'day' : 'week';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/revenue/unified?days=${days}&granularity=${granularity}&status=paid`, { credentials: 'include' });
      if (r.ok) setData(await r.json());
    } finally { setLoading(false); }
  }, [days, granularity]);
  useEffect(() => { load(); }, [load]);

  const visibleDivisions = useMemo(() => DIVISIONS.filter((d) => !hideDivs.has(d.id)), [hideDivs]);
  const toggleDiv = (id) => setHideDivs((s) => { const ns = new Set(s); if (ns.has(id)) ns.delete(id); else ns.add(id); return ns; });

  const chartData = useMemo(() => {
    if (!data?.series?.length) return null;
    const series = data.series;
    const W = 1000, H = 220, PAD_L = 56, PAD_R = 16, PAD_T = 14, PAD_B = 28;
    // Find max stack including only visible divisions
    let maxY = 0;
    for (const pt of series) {
      const stack = visibleDivisions.reduce((s, d) => s + (pt[d.id] || 0), 0);
      if (stack > maxY) maxY = stack;
    }
    if (maxY === 0) maxY = 1;
    const innerW = W - PAD_L - PAD_R, innerH = H - PAD_T - PAD_B;
    const xFor = (i) => PAD_L + (series.length === 1 ? innerW / 2 : (i / (series.length - 1)) * innerW);
    const yFor = (v) => PAD_T + innerH - (v / maxY) * innerH;
    // Build stacked areas
    const stacks = visibleDivisions.map((d) => ({ ...d, points: [] }));
    series.forEach((pt, i) => {
      let cum = 0;
      stacks.forEach((s) => { cum += pt[s.id] || 0; s.points.push({ x: xFor(i), yTop: yFor(cum), yBottom: yFor(cum - (pt[s.id] || 0)) }); });
    });
    const areaPaths = stacks.map((s) => {
      const top = s.points.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)} ${p.yTop.toFixed(1)}`).join(' ');
      const bottom = [...s.points].reverse().map((p) => `L${p.x.toFixed(1)} ${p.yBottom.toFixed(1)}`).join(' ');
      return { ...s, d: `${top} ${bottom} Z` };
    });
    // Y-axis ticks (4 horizontal lines)
    const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => ({ y: yFor(maxY * t), label: fmtUSD(maxY * t) }));
    // X-axis labels (5-6 labels max)
    const xLabelStep = Math.max(1, Math.floor(series.length / 6));
    const xLabels = series.map((pt, i) => i % xLabelStep === 0 ? { x: xFor(i), label: formatBucket(pt.bucket, data.granularity) } : null).filter(Boolean);
    return { W, H, PAD_L, PAD_R, PAD_T, PAD_B, areaPaths, ticks, xLabels, series, xFor, yFor, maxY };
  }, [data, visibleDivisions]);

  if (loading && !data) return <div className="rounded-2xl border border-gray-200 bg-white p-6 text-sm text-gray-500">Loading unified revenue…</div>;
  if (!data) return null;

  const tot = data.totals || {};
  const growth = tot.growth_pct ?? 0;
  const isUp = growth >= 0;

  return (
    <section data-testid="unified-revenue-widget" className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-[#a8871a]">Unified revenue</div>
          <div className="flex items-baseline gap-3 mt-0.5">
            <div className="text-3xl font-extrabold text-[#0e0e10]">{fmtUSD(tot.grand_total)}</div>
            <div className={`inline-flex items-center gap-1 text-[12.5px] font-bold ${isUp ? 'text-emerald-700' : 'text-red-700'}`}>
              {isUp ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
              {isUp ? '+' : ''}{growth}%
              <span className="font-normal text-gray-500">vs prior {data.window_days}d</span>
            </div>
          </div>
          <div className="text-[12px] text-gray-500 mt-1">
            {tot.grand_count} paid transaction{tot.grand_count === 1 ? '' : 's'} across {visibleDivisions.length} division{visibleDivisions.length === 1 ? '' : 's'}.
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {RANGES.map((r) => (
            <button key={r.id} onClick={() => setDays(r.id)} data-testid={`unified-range-${r.id}`}
              className={`inline-flex items-center px-3 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-wider border transition-colors ${
                days === r.id ? 'bg-[#0e0e10] text-white border-[#0e0e10]' : 'bg-white border-gray-200 text-gray-700 hover:border-[#c9a227]'
              }`}>
              {r.label}
            </button>
          ))}
          <button onClick={load} data-testid="unified-refresh"
            className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-[10.5px] font-bold uppercase tracking-wider text-gray-700 hover:bg-gray-200">
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Per-division totals */}
      <div className="grid grid-cols-3 border-b border-gray-100 bg-[#FAFAFA]">
        {DIVISIONS.map((d) => {
          const td = tot.by_division?.[d.id] || { amount: 0, count: 0 };
          const isHidden = hideDivs.has(d.id);
          return (
            <button
              key={d.id}
              onClick={() => toggleDiv(d.id)}
              data-testid={`unified-div-${d.id}`}
              className={`px-5 py-3 text-left border-r border-gray-100 last:border-r-0 transition-opacity ${isHidden ? 'opacity-30' : 'opacity-100'} hover:bg-white`}
              title={isHidden ? `Show ${d.label}` : `Hide ${d.label}`}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <span className="inline-flex h-3 w-3 rounded-sm" style={{ background: d.color }} />
                <d.Icon className="h-3.5 w-3.5 text-gray-600" />
                <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-600">{d.label}</span>
              </div>
              <div className="text-[18px] font-extrabold text-[#0e0e10]">{fmtUSD(td.amount)}</div>
              <div className="text-[11px] text-gray-500">{td.count} txn</div>
            </button>
          );
        })}
      </div>

      {/* Chart */}
      <div className="relative">
        {chartData && (
          <svg
            viewBox={`0 0 ${chartData.W} ${chartData.H}`}
            preserveAspectRatio="none"
            className="w-full h-[220px]"
            onMouseLeave={() => setHoverIdx(null)}
            data-testid="unified-chart"
          >
            {/* Y-axis grid + labels */}
            {chartData.ticks.map((t, i) => (
              <g key={i}>
                <line x1={chartData.PAD_L} x2={chartData.W - chartData.PAD_R} y1={t.y} y2={t.y} stroke="#f0ede5" strokeDasharray="2 3" />
                <text x={chartData.PAD_L - 6} y={t.y + 3} textAnchor="end" className="fill-gray-400" style={{ fontSize: 10, fontFamily: '-apple-system, sans-serif' }}>{t.label}</text>
              </g>
            ))}
            {/* Stacked areas */}
            {chartData.areaPaths.map((s) => (
              <path key={s.id} d={s.d} fill={s.color} fillOpacity={0.85} stroke={s.color} strokeWidth={1.2} />
            ))}
            {/* Hover hit area */}
            {chartData.series.map((pt, i) => (
              <rect
                key={`hit-${i}`}
                x={chartData.xFor(i) - 8}
                y={chartData.PAD_T}
                width={16}
                height={chartData.H - chartData.PAD_T - chartData.PAD_B}
                fill="transparent"
                onMouseEnter={() => setHoverIdx(i)}
                style={{ cursor: 'crosshair' }}
              />
            ))}
            {/* Hover guideline */}
            {hoverIdx !== null && chartData.series[hoverIdx] && (
              <line
                x1={chartData.xFor(hoverIdx)} x2={chartData.xFor(hoverIdx)}
                y1={chartData.PAD_T} y2={chartData.H - chartData.PAD_B}
                stroke="#0e0e10" strokeOpacity={0.4} strokeDasharray="2 3"
              />
            )}
            {/* X-axis labels */}
            {chartData.xLabels.map((l, i) => (
              <text key={i} x={l.x} y={chartData.H - 8} textAnchor="middle" className="fill-gray-400" style={{ fontSize: 10, fontFamily: '-apple-system, sans-serif' }}>
                {l.label}
              </text>
            ))}
          </svg>
        )}
        {/* Hover tooltip */}
        {hoverIdx !== null && chartData?.series[hoverIdx] && (
          <div
            data-testid="unified-tooltip"
            className="absolute pointer-events-none rounded-xl bg-[#0e0e10] text-white px-3 py-2 text-[11.5px] shadow-2xl"
            style={{
              left: `${(chartData.xFor(hoverIdx) / chartData.W) * 100}%`,
              transform: hoverIdx > chartData.series.length / 2 ? 'translateX(-105%)' : 'translateX(5%)',
              top: 8,
            }}
          >
            <div className="font-bold mb-1">{formatBucket(chartData.series[hoverIdx].bucket, data.granularity)}</div>
            {visibleDivisions.map((d) => (
              <div key={d.id} className="flex items-center gap-2">
                <span className="inline-flex h-2 w-2 rounded-sm" style={{ background: d.color }} />
                <span className="text-white/70">{d.label}:</span>
                <span className="font-bold tabular-nums">{fmtUSD(chartData.series[hoverIdx][d.id])}</span>
              </div>
            ))}
            <div className="border-t border-white/20 mt-1 pt-1 flex items-center justify-between gap-3">
              <span className="text-white/70">Total</span>
              <span className="font-bold tabular-nums">{fmtUSD(chartData.series[hoverIdx].total)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Top items */}
      <div className="grid grid-cols-1 md:grid-cols-3 border-t border-gray-100">
        {DIVISIONS.map((d) => {
          const items = data.top_items?.[d.id] || [];
          return (
            <div key={d.id} className="px-5 py-3 border-r border-gray-100 last:border-r-0" data-testid={`unified-top-${d.id}`}>
              <div className="flex items-center gap-1.5 mb-2">
                <span className="inline-flex h-2.5 w-2.5 rounded-sm" style={{ background: d.color }} />
                <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-gray-600">Top {d.label.toLowerCase()}</span>
              </div>
              {items.length === 0 ? (
                <div className="text-[12px] text-gray-400 italic">No revenue in this window.</div>
              ) : (
                <ul className="space-y-1">
                  {items.map((it, i) => (
                    <li key={`${d.id}-${it.slug}-${i}`} className="flex items-center justify-between text-[12px]">
                      <span className="truncate font-semibold text-[#0e0e10] flex-1 mr-2" title={it.name}>{it.name}</span>
                      <span className="font-mono tabular-nums text-gray-600 shrink-0">{fmtUSD(it.amount)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      <footer className="px-5 py-2.5 bg-[#FAFAFA] text-[11px] text-gray-500 flex items-center gap-1.5 border-t border-gray-100">
        <Activity className="h-3 w-3" />
        Live aggregate from <code className="bg-white px-1 rounded">payment_transactions</code> (Academy + Systems) and <code className="bg-white px-1 rounded">services_transactions</code> (e-store).
        <DollarSign className="h-3 w-3 ml-auto text-gray-400" />
        <span>USD</span>
      </footer>
    </section>
  );
}
